import {Button, Card, DatePicker, Divider, Input, message, Modal, Select, Tooltip, Upload} from "antd";
import {
    CloseOutlined,
    DeleteOutlined,
    DownloadOutlined,
    EditOutlined,
    EyeOutlined,
    SaveFilled,
    UploadOutlined,
    UserOutlined
} from "@ant-design/icons";
import {UtilsService} from "../../services/Utils.service";
import Title from "antd/es/typography/Title";
import {DocumentMetadata, DocumentType, DocumentTypeCode, Person, TemplateDocumentType} from "../../model/models";
import {forwardRef, useEffect, useImperativeHandle, useState} from "react";
import {HttpService} from "../../services/http/Http.service";
import {HttpErrorService} from "../../services/http/HttpError.service";
import {TemplateUtils} from "../../services/utils/TemplateUtils";
import UserDetailsButton from "./UserDetailsButton";
import dayjs from "dayjs";
import ConfirmModal from "../../components/confirmModal/ConfirmModal";
import {AuthService} from "../../services/Auth.service";

interface ProfileCardProps {
    person?: Person | undefined,
    documentSectionOpened?: boolean,
    setPerson?: (value: (((prevState: (Person | undefined)) => (Person | undefined)) | Person | undefined)) => void
    simulatorId?: number;
    hasPaidInstallments?: boolean;
}

export type ProfileCardRef = {
    fetchDocuments: () => void;
};

interface ProfileCardProps {
    refreshPractices?: () => Promise<void>
}

const ProfileCard = forwardRef<ProfileCardRef, ProfileCardProps>(({
                                                                      person,
                                                                      documentSectionOpened,
                                                                      setPerson,
                                                                      simulatorId,
                                                                      hasPaidInstallments,
                                                                      refreshPractices
                                                                  }, ref) => {
        const [isEditing, setIsEditing] = useState(false);
        const [formState, setFormState] = useState({
            caller: person?.contact?.name || "",
            phone: person?.contact?.phone || "",
            email: person?.contact?.email || "",
        });

        const [documents, setDocuments] = useState<DocumentMetadata[]>([]);
        const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);
        const [tipologie, setTipologie] = useState<DocumentType[]>([]);
        const [selectedTipologia, setSelectedTipologia] = useState<DocumentType | null>(null);
        const [uploadingFile, setUploadingFile] = useState<File | null>(null);
        const [fileList, setFileList] = useState<any[]>([]);
        const [heirs, setHeirs] = useState<Person[]>([]);
        const [deceasedPersons, setDeceasedPersons] = useState<Person[]>([]);
        const [deleteHeirModalVisible, setDeleteHeirModalVisible] = useState(false);
        const [heirToDeleteId, setHeirToDeleteId] = useState<number | null>(null);
        const [isDeletingHeir, setIsDeletingHeir] = useState(false);
        const [userUploadDate, setUserUploadDate] = useState<dayjs.Dayjs | null>(null);
        const [isUploadingDocument, setIsUploadingDocument] = useState(false);

        const [isEditingDomicile, setIsEditingDomicile] = useState(false);
        const [domicileState, setDomicileState] = useState({
            domicileAt: person?.domicileAt || "",
            domicileAddress: person?.domicileAddress || "",
            domicileHouseNumber: person?.domicileHouseNumber || "",
            domicilePostalCode: person?.domicilePostalCode || "",
            domicileCity: person?.domicileCity || "",
            domicileProvince: person?.domicileProvince || "",
        });


        const [domicileModalOpen, setDomicileModalOpen] = useState(false);
        const [savingSaveOnly, setSavingSaveOnly] = useState(false);
        const [savingSaveAndRework, setSavingSaveAndRework] = useState(false);

        useEffect(() => {
            setDomicileState({
                domicileAt: person?.domicileAt || "",
                domicileAddress: person?.domicileAddress || "",
                domicileHouseNumber: person?.domicileHouseNumber || "",
                domicilePostalCode: person?.domicilePostalCode || "",
                domicileCity: person?.domicileCity || "",
                domicileProvince: person?.domicileProvince || "",
            });
            setIsEditingDomicile(false);
        }, [person?.id]);


        useEffect(() => {
            if (person?.contact) {
                setFormState({
                    caller: person.contact.name || "",
                    phone: person.contact.phone || "",
                    email: person.contact.email || "",
                });
            }
            setHeirs([]);
            if (person?.active === false) {
                fetchHeirInfo(person.id);
            }
        }, [person?.id, person?.active]);

        const fetchHeirInfo = async (personId: number) => {
            try {
                if (person?.active !== false) return;
                const res = await HttpService.getInstanceAxios().get(`/succession/person/${personId}/successors`);
                setHeirs(res.data);
            } catch (err) {
                HttpErrorService.handle("Errore nella ricerca degli eredi", err, true);
            }
        };

        useEffect(() => {
            setDeceasedPersons([]);

            const fetchDeceasedPersons = async () => {
                if (!person?.id) return;
                try {
                    const res = await HttpService.getInstanceAxios().get(`/succession/successor/${person.id}/deceased`);
                    setDeceasedPersons(res.data);
                } catch (err) {
                    HttpErrorService.handle("Errore nella ricerca dei deceduti di cui è erede", err, true);
                }
            };
            if (person?.active) {
                fetchDeceasedPersons();
            }
        }, [person?.id, person?.active]);


        useEffect(() => {
            if (isUploadModalOpen) {
                fetchTipologie();
            }
        }, [isUploadModalOpen]);

        const fetchDocuments = async () => {
            try {
                const response = await HttpService.getInstanceAxios().get(`/person/${person?.id}/document/simulator/${simulatorId}/all`);
                setDocuments(response.data);
            } catch (error) {
                message.error("Errore durante il recupero dei documenti");
            }
        };

        useImperativeHandle(ref, () => ({
            fetchDocuments,
        }));

        useEffect(() => {
            if (!person?.id || !simulatorId || !documentSectionOpened) return;
            fetchDocuments();
        }, [person?.id, simulatorId, documentSectionOpened]);


        // ---- DOMICILE: shared save helper (used by both modal buttons) ----
        const doUpdateDomicile = async (): Promise<boolean> => {
            if (domicileState.domicilePostalCode && !/^\d{5}$/.test(domicileState.domicilePostalCode)) {
                message.error("Il CAP deve contenere 5 cifre.");
                return false;
            }

            const body = {
                locatedAt: domicileState.domicileAt || null,
                address: domicileState.domicileAddress || null,
                houseNumber: domicileState.domicileHouseNumber || null,
                city: domicileState.domicileCity || null,
                postalCode: domicileState.domicilePostalCode || null,
                province: domicileState.domicileProvince?.toUpperCase() || null,
            };

            try {
                await HttpService.getInstanceAxios().put(`/person/${person?.id}/updateDomicileAddress`, body);
                // sync UI state
                if (setPerson && person) {
                    setPerson({
                        ...person,
                        domicileAt: domicileState.domicileAt,
                        domicileAddress: domicileState.domicileAddress,
                        domicileHouseNumber: domicileState.domicileHouseNumber,
                        domicileCity: domicileState.domicileCity,
                        domicilePostalCode: domicileState.domicilePostalCode,
                        domicileProvince: (domicileState.domicileProvince || "").toUpperCase(),
                    });
                }
                return true;
            } catch (error) {
                HttpErrorService.handle(
                    "Errore durante l'aggiornamento dell'indirizzo di notifica.",
                    error,
                    true
                );
                return false;
            }
        };

        const saveDomicileOnly = async () => {
            if (savingSaveOnly || savingSaveAndRework) return;
            setSavingSaveOnly(true);
            try {
                const ok = await doUpdateDomicile();
                if (!ok) return;
                message.success("Indirizzo di notifica aggiornato!");
                setIsEditingDomicile(false);
                setDomicileModalOpen(false);
            } finally {
                setSavingSaveOnly(false);
            }
        };

        const saveDomicileAndRework = async () => {
            if (savingSaveOnly || savingSaveAndRework) return;
            if (!simulatorId || !person?.id) {
                message.error("Impossibile elaborare: simulatorId o personId mancante.");
                return;
            }
            setSavingSaveAndRework(true);
            try {
                const ok = await doUpdateDomicile();
                if (!ok) return;

                await HttpService.getInstanceAxios().post(`/file-ts/${simulatorId}/rework/${person.id}`);
                message.success("Indirizzo aggiornato e pratica rielaborata!");
                setIsEditingDomicile(false);
                setDomicileModalOpen(false);
            } catch (error) {
                HttpErrorService.handle("Errore durante la rielaborazione della pratica.", error, true);
            } finally {
                setSavingSaveAndRework(false);
            }
        };


        const handleCancelDomicile = () => {
            setDomicileState({
                domicileAt: person?.domicileAt || "",
                domicileAddress: person?.domicileAddress || "",
                domicileHouseNumber: person?.domicileHouseNumber || "",
                domicilePostalCode: person?.domicilePostalCode || "",
                domicileCity: person?.domicileCity || "",
                domicileProvince: person?.domicileProvince || "",
            });
            setIsEditingDomicile(false);
        };


        const handleSave = async () => {
            if (formState.email.length > 0 && !UtilsService.isValidEmail(formState.email)) {
                message.error("Inserisci un'email valida.");
                return;
            }

            if (formState.phone.length > 0 && !UtilsService.isValidPhone(formState.phone)) {
                message.error("Il numero di telefono deve contenere tra 8 e 11 cifre.");
                return;
            }
            if (person) {
                try {
                    await HttpService.getInstanceAxios().put(`/contact/${person.id}`, {
                        name: formState.caller,
                        phone: formState.phone,
                        email: formState.email
                    });
                    message.success("Contatto aggiornato con successo!");
                    setIsEditing(false);
                    if (setPerson && person) {
                        setPerson({
                            ...person,
                            contact: {
                                ...person.contact,
                                name: formState.caller,
                                email: formState.email,
                                phone: formState.phone
                            }
                        });
                    }
                } catch (error) {
                    HttpErrorService.handle("Errore durante l'aggiornamento del contatto.", error, true)
                }
            }
        };

        const handleCancel = () => {
            setIsEditing(false);
        };

        const fetchTipologie = async () => {
            try {
                const response = await HttpService.getInstanceAxios().get("/document-type");
                const visibleDocuments = response.data.filter((item: any) => item.visible === true);
                setTipologie(visibleDocuments);
            } catch (error) {
                HttpErrorService.handle("Errore durante il recupero delle tipologie di documento", error, true)
            }
        };

        const confirmDelete = (documentTypeCode: string, uuid: string) => {
            let text;
            if (documentTypeCode === TemplateDocumentType.RICALCOLO_RICETTE || documentTypeCode === TemplateDocumentType.RICALCOLO_SEPARATO || documentTypeCode === TemplateDocumentType.RICALCOLO_LR_NO_RATEPAGATE || documentTypeCode === TemplateDocumentType.RICALCOLO_LR_RATEPAGATE || documentTypeCode === TemplateDocumentType.NOTA_ARCHIVIAZIONE || documentTypeCode === DocumentTypeCode.ACCOGLIMENTO_RATE || documentTypeCode === DocumentTypeCode.RATE_NON_PAGATE || documentTypeCode === DocumentTypeCode.RATE_NON_PAGATE_MONTE) {
                text = 'Sei sicuro di voler eliminare questo documento? Questa operazione riporterà la pratica in stato SOSPESO'
            } else {
                text = 'Sei sicuro di voler eliminare questo documento?'
            }
            Modal.confirm({
                title: <span style={{fontWeight: 600}}>Conferma eliminazione</span>,
                content: <span>{text}</span>,
                okText: "Continua",
                cancelText: "Annulla",
                okButtonProps: {
                    style: {backgroundColor: "#007A33", borderColor: "#007A33", color: "#fff", fontWeight: 500}
                },
                cancelButtonProps: {style: {borderRadius: 6, fontWeight: 500}},
                centered: false,
                icon: null,
                onOk: async () => {
                    try {
                        await HttpService.getInstanceAxios().delete(`/person/${person?.id}/document/simulator/${simulatorId}/delete/${uuid}`);
                        message.success("Documento eliminato");
                        setDocuments(prev => prev.filter(doc => doc.uuid !== uuid));
                    } catch (e) {
                        message.error("Errore durante l'eliminazione");
                    } finally {
                        if (refreshPractices) {
                            await refreshPractices()
                        }
                    }
                }
            });
        };

        const handleUploadDocument = async () => {
            if (!uploadingFile || !selectedTipologia || !userUploadDate) {
                message.error("Compila tutti i campi e carica un file");
                return;
            }
            const formData = new FormData();
            formData.append("file", uploadingFile);

            const now = dayjs();
            const dateWithTime = userUploadDate
                ? userUploadDate.hour(now.hour()).minute(now.minute()).second(now.second())
                : null;

            const dateParam = dateWithTime ? encodeURIComponent(dateWithTime.format("YYYY-MM-DDTHH:mm:ss")) : "";


            const url = `/person/${person?.id}/document/upload/simulator/${simulatorId}?tipologia=${selectedTipologia}${
                dateParam ? `&userUploadDate=${dateParam}` : ""
            }`;

            setIsUploadingDocument(true);
            try {
                const response = await HttpService.getInstanceAxios().post(url, formData);
                message.success("Documento caricato con successo");
                setIsUploadModalOpen(false);
                setSelectedTipologia(null);
                setUploadingFile(null);
                setDocuments(prev => {
                    const updated = [response.data, ...prev];
                    return updated.sort((a, b) => {
                        const dateA = a.userUploadDate ? new Date(a.userUploadDate).getTime() : 0;
                        const dateB = b.userUploadDate ? new Date(b.userUploadDate).getTime() : 0;
                        return dateB - dateA; // newest first
                    });
                });
                setFileList([]);
                setUserUploadDate(null);
            } catch (e) {
                message.error("Errore nel caricamento del documento");
            } finally {
                setIsUploadingDocument(false);
            }
        };

        const confirmDeleteHeir = (heirId: number) => {
            setHeirToDeleteId(heirId);
            setDeleteHeirModalVisible(true);
        };

        const handleDeleteHeir = async () => {
            if (heirToDeleteId === null) return;
            setIsDeletingHeir(true);
            try {
                await HttpService.getInstanceAxios().delete(`/succession/person/${person?.id}/successor/${heirToDeleteId}`);
                setHeirs(prev => prev.filter(h => h.id !== heirToDeleteId));
                message.success("Erede rimosso");
                setDeleteHeirModalVisible(false);
                setHeirToDeleteId(null);
            } catch (e) {
                message.error("Errore durante la rimozione dell'erede");
            } finally {
                setIsDeletingHeir(false);
            }
        };

        const getFinalName = (doc: { originalName: string; extension: string }) => {
            const ext = (doc.extension || "").trim().toLowerCase();
            const original = (doc.originalName || "").trim();

            return original.toLowerCase().endsWith(`.${ext}`)
                ? original
                : `${original}.${doc.extension}`;
        };


        return (
            <>
                {person &&
                    <Card
                        title={
                            <Title
                                level={3}
                                style={{
                                    wordBreak: "break-word",
                                    whiteSpace: "normal",
                                    lineHeight: 1.2,
                                    maxWidth: 400,
                                }}
                            >
                                <UserOutlined/> {person?.firstName} {person?.lastName}
                            </Title>
                        }
                        style={{
                            background: "#FAFAFA",
                            width: "fit-content",
                            minWidth: 370,
                            maxWidth: 500,
                            flexShrink: 0,
                            position: "sticky",
                            top: 20,
                        }}
                    >

                        <div style={{display: "flex", flexDirection: "column", gap: "2px"}}>
                            <div className="data-container"><strong>Codice Fiscale:</strong>{person.fiscalCode}</div>
                            <div className="data-container"><strong>Data di
                                nascita:</strong>{UtilsService.formatDateString(person?.dateOfBirth)}</div>
                            <div className="data-container"><strong>Luogo di nascita:</strong>{person?.placeOfBirth}</div>
                            <div className="data-container">
                                <strong>Indirizzo:</strong>{person.residenceAddress} {person.residenceHouseNumber}</div>
                            <div className="data-container"><strong>CAP:</strong> {person.residencePostalCode}</div>
                            <div className="data-container"><strong>Città:</strong>{person.residenceCity}</div>
                            <div className="data-container"><strong>Provincia:</strong> {person.residenceProvince}</div>
                        </div>

                        {AuthService.isAdmin() &&
                            <div style={{display: "flex", alignItems: "center", margin: "16px 0"}}>
                                <div style={{flex: 1}}>
                                    <Divider>Indirizzo di notifica</Divider>
                                </div>

                                {isEditingDomicile ? (
                                    <>
                                        <Tooltip title="Salva">
                                            <Button
                                                icon={<SaveFilled/>}
                                                type="primary"
                                                onClick={() => setDomicileModalOpen(true)}
                                                style={{marginLeft: 8}}
                                            />
                                        </Tooltip>
                                        <Tooltip title="Annulla">
                                            <Button
                                                icon={<CloseOutlined/>}
                                                onClick={handleCancelDomicile}
                                                style={{marginLeft: 8}}
                                            />
                                        </Tooltip>
                                    </>
                                ) : (
                                    <Tooltip title="Aggiorna indirizzo di notifica">
                                        <Button
                                            icon={<EditOutlined/>}
                                            type="primary"
                                            onClick={() => setIsEditingDomicile(true)}
                                            style={{marginLeft: 16}}
                                        />
                                    </Tooltip>
                                )}
                            </div>
                        }

                        <div style={{display: "flex", flexDirection: "column", gap: 2}}>
                            <div className="data-container">
                                <strong>Presso:</strong>{" "}
                                {isEditingDomicile ? (
                                    <Input
                                        style={{width: 150, float: "right"}}
                                        value={domicileState.domicileAt}
                                        onChange={(e) =>
                                            setDomicileState((s) => ({...s, domicileAt: e.target.value}))
                                        }
                                    />
                                ) : (
                                    person.domicileAt || ""
                                )}
                            </div>
                            <div className="data-container">
                                <strong>Indirizzo:</strong>{" "}
                                {isEditingDomicile ? (
                                    <Input
                                        style={{width: 150, float: "right"}}
                                        value={domicileState.domicileAddress}
                                        onChange={(e) =>
                                            setDomicileState((s) => ({...s, domicileAddress: e.target.value}))
                                        }
                                    />
                                ) : (
                                    person.domicileAddress || ""
                                )}
                            </div>
                            <div className="data-container">
                                <strong>Civico/interno:</strong>{" "}
                                {isEditingDomicile ? (
                                    <Input
                                        style={{width: 150, float: "right"}}
                                        value={domicileState.domicileHouseNumber}
                                        onChange={(e) =>
                                            setDomicileState((s) => ({
                                                ...s,
                                                domicileHouseNumber: e.target.value,
                                            }))
                                        }
                                    />
                                ) : (
                                    person.domicileHouseNumber || ""
                                )}
                            </div>
                            <div className="data-container">
                                <strong>CAP:</strong>{" "}
                                {isEditingDomicile ? (
                                    <Input
                                        style={{width: 150, float: "right"}}
                                        maxLength={5}
                                        value={domicileState.domicilePostalCode}
                                        onChange={(e) =>
                                            setDomicileState((s) => ({
                                                ...s,
                                                domicilePostalCode: e.target.value.replace(/\D/g, ""),
                                            }))
                                        }
                                    />
                                ) : (
                                    person.domicilePostalCode || ""
                                )}
                            </div>
                            <div className="data-container">
                                <strong>Città:</strong>{" "}
                                {isEditingDomicile ? (
                                    <Input
                                        style={{width: 150, float: "right"}}
                                        value={domicileState.domicileCity}
                                        onChange={(e) =>
                                            setDomicileState((s) => ({...s, domicileCity: e.target.value}))
                                        }
                                    />
                                ) : (
                                    person.domicileCity || ""
                                )}
                            </div>
                            <div className="data-container">
                                <strong>Provincia:</strong>{" "}
                                {isEditingDomicile ? (
                                    <Input
                                        style={{width: 150, float: "right"}}
                                        maxLength={2}
                                        value={domicileState.domicileProvince}
                                        onChange={(e) =>
                                            setDomicileState((s) => ({
                                                ...s,
                                                domicileProvince: e.target.value.toUpperCase(),
                                            }))
                                        }
                                    />
                                ) : (
                                    person.domicileProvince || ""
                                )}
                            </div>
                        </div>

                        <Modal
                            title="Conferma salvataggio indirizzo"
                            open={domicileModalOpen}
                            onCancel={() => !(savingSaveOnly || savingSaveAndRework) && setDomicileModalOpen(false)}
                            footer={[
                                <Tooltip
                                    key="rework-tt"
                                    title={
                                        !simulatorId || !person?.id
                                            ? "Seleziona un simulatore per elaborare"
                                            : hasPaidInstallments
                                                ? "Operazione non disponibile: è presente almeno una rata pagata"
                                                : ""
                                    }
                                >
                                 <span>
                                       <Button
                                           key="saveAndRework"
                                           type="primary"
                                           onClick={saveDomicileAndRework}
                                           loading={savingSaveAndRework}
                                           disabled={savingSaveOnly || !simulatorId || !person?.id || !!hasPaidInstallments}
                                           style={{backgroundColor: "#007A33", borderColor: "#007A33"}}
                                       >
                                         Salva ed elabora
                                         </Button>
                                </span>
                                </Tooltip>,
                                <Button
                                    key="saveOnly"
                                    onClick={saveDomicileOnly}
                                    loading={savingSaveOnly}
                                    disabled={savingSaveAndRework}
                                >
                                    Salva
                                </Button>
                            ]}
                            destroyOnClose
                        >
                            <p>Vuoi salvare il nuovo indirizzo di notifica oppure vuoi anche rielaborare anche la
                                pratica?</p>
                        </Modal>


                        <div style={{display: "flex", alignItems: "center", margin: "16px 0"}}>
                            <div style={{flex: 1}}><Divider>Contatti</Divider></div>
                            {isEditing ? (
                                <>
                                    <Tooltip title="Salva">
                                        <Button icon={<SaveFilled/>} type="primary" onClick={handleSave}
                                                style={{marginLeft: 8}}/>
                                    </Tooltip>
                                    <Tooltip title="Annulla">
                                        <Button icon={<CloseOutlined/>} onClick={handleCancel} style={{marginLeft: 8}}/>
                                    </Tooltip>
                                </>
                            ) : (
                                <Tooltip title="Aggiorna contatti">
                                    <Button icon={<EditOutlined/>} type="primary" onClick={() => setIsEditing(true)}
                                            style={{marginLeft: 16}}/>
                                </Tooltip>
                            )}
                        </div>

                        <div className="data-container">
                            <strong>Denominazione:</strong>{" "}
                            {isEditing ? (
                                <Input
                                    style={{width: 100, float: "right"}}
                                    value={formState.caller}
                                    onChange={(e) => setFormState({...formState, caller: e.target.value})}
                                />
                            ) : (
                                person?.contact?.name || ""
                            )}
                        </div>
                        <div className="data-container">
                            <strong>Telefono:</strong>{" "}
                            {isEditing ? (
                                <Input
                                    style={{width: 100, float: "right"}}
                                    value={formState.phone}
                                    onChange={(e) => setFormState({...formState, phone: e.target.value})}
                                />
                            ) : (
                                person?.contact?.phone || ""
                            )}
                        </div>
                        <div className="data-container">
                            <strong>Email:</strong>{" "}
                            {isEditing ? (
                                <Input
                                    style={{width: 100, float: "right"}}
                                    value={formState.email}
                                    onChange={(e) => setFormState({...formState, email: e.target.value})}
                                />
                            ) : (
                                person?.contact?.email || ""
                            )}
                        </div>

                        {documentSectionOpened && (
                            <>
                                <div style={{display: "flex", alignItems: "center", margin: "16px 0"}}>
                                    <div style={{flex: 1}}><Divider
                                        style={{borderColor: "#D9D9D9"}}>Documentazione</Divider></div>
                                    <Tooltip title={"Scarica ZIP"}>
                                        <Button
                                            icon={<DownloadOutlined/>}
                                            onClick={() =>
                                                TemplateUtils.downloadDocumentsAsZip([
                                                    {simulatorId: simulatorId!, personId: person?.id!}
                                                ], false, undefined)

                                            }
                                            disabled={documents.length === 0}
                                            style={{
                                                marginLeft: 8,
                                                backgroundColor: "#00703C",
                                                borderColor: "#00703C",
                                                color: "white"
                                            }}
                                        />
                                    </Tooltip>


                                    <Tooltip title={"Aggiungi documentazione"}>
                                        <Button type="primary" icon={<UploadOutlined/>}
                                                onClick={() => setIsUploadModalOpen(true)} style={{
                                            marginLeft: 16,
                                            backgroundColor: "#00703C",
                                            borderColor: "#00703C"
                                        }}/>
                                    </Tooltip>
                                </div>

                                {documents.map((doc) => (
                                    <div key={doc.uuid} style={{
                                        display: "flex",
                                        marginTop: "20px",
                                        justifyContent: "space-between",
                                        alignItems: "flex-start",
                                        borderRadius: "12px",
                                        backgroundColor: "#FAFAFA",
                                        width: "100%",
                                        marginBottom: "10px"
                                    }}>
                                        <div style={{flex: 1}}>
                                            <div style={{
                                                fontWeight: 500,
                                                fontSize: "14px",
                                                wordBreak: "break-word"
                                            }}>
                                                {getFinalName(doc)}
                                            </div>
                                            <div style={{
                                                fontSize: "12px",
                                                color: "#888"
                                            }}>{UtilsService.formatDateString(doc.userUploadDate)}</div>
                                        </div>
                                        <div style={{display: "flex", gap: "8px", marginLeft: 10}}>
                                            <Tooltip title="Visualizza">
                                                <Button icon={<EyeOutlined/>} size="middle"
                                                        onClick={() => TemplateUtils.previewDocument(doc.uuid, doc.originalName, person?.id!, simulatorId!)}/>
                                            </Tooltip>
                                            {!doc.signed && (
                                                <Tooltip title="Elimina">
                                                    <Button icon={<DeleteOutlined/>} size="middle" type="primary"
                                                            onClick={() => confirmDelete(doc.documentTypeCode, doc.uuid)}/>
                                                </Tooltip>
                                            )}

                                        </div>
                                    </div>
                                ))}

                                <Modal
                                    open={isUploadModalOpen}
                                    onOk={handleUploadDocument}
                                    onCancel={() => {
                                        setIsUploadModalOpen(false);
                                        setSelectedTipologia(null);
                                        setUploadingFile(null);
                                        setFileList([]);
                                    }}
                                    okText="Continua"
                                    cancelText="Annulla"
                                    okButtonProps={{loading: isUploadingDocument}}
                                    cancelButtonProps={{disabled: isUploadingDocument}}
                                >
                                    <p><strong>Tipologia documento</strong></p>
                                    <Select
                                        style={{width: "100%"}}
                                        placeholder="Seleziona tipologia"
                                        value={selectedTipologia}
                                        onChange={(value) => setSelectedTipologia(value)}
                                        dropdownRender={menu => (
                                            <>
                                                {menu}
                                            </>
                                        )}
                                    >
                                        {tipologie.map(t => (
                                            <Select.Option key={t.code} value={t.code}>{t.name}</Select.Option>
                                        ))}
                                    </Select>

                                    <Divider/>
                                    <Upload beforeUpload={(file) => {
                                        setUploadingFile(file);
                                        setFileList([file]);
                                        return false;
                                    }}
                                            onRemove={() => {
                                                setUploadingFile(null);
                                                setFileList([]);
                                            }}
                                            fileList={fileList}
                                            showUploadList={true}
                                            maxCount={1}
                                    >
                                        <Button icon={<UploadOutlined/>}>Carica documento</Button>
                                    </Upload>
                                    <p><strong>Data documento</strong></p>
                                    <DatePicker
                                        style={{width: "100%"}}
                                        value={userUploadDate}
                                        onChange={setUserUploadDate}
                                        format="DD/MM/YYYY"
                                        placeholder="Seleziona data"
                                    />


                                    <Divider/>

                                </Modal>
                            </>
                        )}
                        {heirs.length > 0 && (
                            <>
                                <div style={{display: "flex", alignItems: "center", margin: "16px 0"}}>
                                    <div style={{flex: 1}}>
                                        <Divider
                                            style={{borderColor: "#D9D9D9"}}>Ered{heirs.length > 1 ? "i" : "e"}</Divider>
                                    </div>
                                </div>
                                {heirs.map((heir) => (
                                    <div key={heir.id} style={{
                                        backgroundColor: "#FAFAFA",
                                        borderRadius: "12px",
                                        padding: "10px",
                                        marginBottom: "10px"
                                    }}>
                                        <div style={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center"
                                        }}>
                                            <div>
                                                <div><strong>Nome e Cognome:</strong> {heir.firstName} {heir.lastName}</div>
                                                <div><strong>Codice Fiscale:</strong> {heir.fiscalCode}</div>
                                            </div>
                                            <div style={{display: "flex", gap: 8, alignItems: "center"}}>
                                                <UserDetailsButton personID={heir.id} forceReload={true}/>
                                                <Tooltip title="Rimuovi erede">
                                                    <Button
                                                        icon={<CloseOutlined/>}
                                                        danger
                                                        shape={"circle"}
                                                        onClick={() => confirmDeleteHeir(heir.id)}
                                                    />
                                                </Tooltip>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}


                        {deceasedPersons.length > 0 && (
                            <>
                                <div style={{display: "flex", alignItems: "center", margin: "16px 0"}}>
                                    <div style={{flex: 1}}>
                                        <Divider style={{borderColor: "#D9D9D9"}}>Erede di</Divider>
                                    </div>
                                    <div style={{marginLeft: "15px"}}>
                                        <UserDetailsButton personID={deceasedPersons[0].id} forceReload={true}/>
                                    </div>
                                </div>
                                {deceasedPersons.map((deceased) => (
                                    <div key={deceased.id} style={{
                                        backgroundColor: "#FAFAFA",
                                        borderRadius: "12px",
                                        padding: "10px",
                                        marginBottom: "10px"
                                    }}>
                                        <div style={{marginBottom: "4px"}}>
                                            <strong>Nome e Cognome:</strong> {deceased.firstName} {deceased.lastName}
                                        </div>
                                        <div>
                                            <strong>Codice Fiscale:</strong> {deceased.fiscalCode}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                        <ConfirmModal
                            visible={deleteHeirModalVisible}
                            title="Rimuovi erede"
                            message="Sei sicuro di voler rimuovere questo erede?"
                            confirmText="Rimuovi"
                            loading={isDeletingHeir}
                            onConfirm={handleDeleteHeir}
                            onCancel={() => {
                                if (isDeletingHeir) return;
                                setDeleteHeirModalVisible(false);
                                setHeirToDeleteId(null);
                            }}
                        />
                    </Card>

                }
            </>
        )
    }
)
export default ProfileCard;
